import { useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

interface UseCategoriesMissionConfigOpts {
  categoriesMission: { id: number; nom: string; created_at?: string }[];
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
  loadAllData: () => Promise<void>;
}

/** Hook extrait de SystemConfiguration.tsx : catégories de mission. */
export function useCategoriesMissionConfig({
  categoriesMission,
  setError,
  setSuccess,
  loadAllData,
}: UseCategoriesMissionConfigOpts) {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [editingMissionCategoryId, setEditingMissionCategoryId] = useState<number | null>(null);

  const resetMissionCategoryForm = () => {
    setEditingMissionCategoryId(null);
    setNewCategoryName('');
  };

  const handleEditMissionCategory = (c: { id: number; nom: string }) => {
    setEditingMissionCategoryId(c.id);
    setNewCategoryName(c.nom);
  };

  const handleCancelEditMissionCategory = () => {
    resetMissionCategoryForm();
  };

  const handleSaveMissionCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    setCreatingCategory(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Token d'authentification manquant");
        return;
      }
      const apiUrl = getApiBaseUrl();
      const isEdit = editingMissionCategoryId !== null;
      const endpoint = isEdit
        ? `${apiUrl}/api/cta/categories-mission/${editingMissionCategoryId}`
        : `${apiUrl}/api/cta/categories-mission`;
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nom: trimmed }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess(isEdit ? 'Catégorie mise à jour.' : 'Catégorie créée.');
        resetMissionCategoryForm();
        await loadAllData();
      } else {
        setError(data.error || 'Erreur lors de la sauvegarde.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteMissionCategory = async (id: number) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("Token d'authentification manquant");
        return;
      }
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/cta/categories-mission/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess('Catégorie supprimée.');
        if (editingMissionCategoryId === id) resetMissionCategoryForm();
        await loadAllData();
      } else {
        setError(data.error || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression.');
    }
  };

  return {
    newCategoryName,
    setNewCategoryName,
    creatingCategory,
    editingMissionCategoryId,
    handleEditMissionCategory,
    handleCancelEditMissionCategory,
    handleSaveMissionCategory,
    handleDeleteMissionCategory,
  };
}
